import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import _TextField from "@mui/material/TextField";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _InputAdornment from "@mui/material/InputAdornment";
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emptyStateIllus from '../assets/images/trophy-illus-2.svg';
import { GeogroupService } from '../services';
import { CommunityItem } from './community-item';
import CreateCommunityCard from './create-community-card';
import { SearchIcon } from './icons';
export function CommunitySearch({
  Button,
  Dialog,
  currentUser,
  userGeogroups,
  createCommunityCardActions,
  handleClick,
  handleCommunityJoined,
  handleTryToJoinAsGuest
}) {
  const [initialized, setInitialized] = useState(false);
  const [geogroups, setGeogroups] = useState([]);
  const [joinedGeogroups, setJoinedGeogroups] = useState();
  const [search, setSearch] = useState('');
  const fetch = useMemo(() => debounce(async (search, callback) => {
    const {
      geogroups
    } = await GeogroupService.getGeogroups({
      pageSize: 100,
      page: 1,
      search,
      types: ['city', 'association', 'school', 'company', 'private', 'other']
    });
    callback(geogroups);
  }, 700), []);
  const {
    t
  } = useTranslation();
  useEffect(() => {
    setInitialized(true);
  }, []);
  useEffect(() => {
    if (userGeogroups) {
      setJoinedGeogroups([...userGeogroups].filter(({
        status
      }) => status === 'manuallyJoin'));
    }
    return () => setJoinedGeogroups(undefined);
  }, [userGeogroups]);
  useEffect(() => {
    if (!initialized) return;
    let active = true;
    if (search && search.length > 1) {
      setGeogroups(undefined);
      fetch(search, geogroups => {
        if (active) setGeogroups(geogroups);
      });
    }
    return () => {
      active = false;
      setGeogroups([]);
    };
  }, [initialized, search, fetch]);
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 5
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    margin: "dense",
    onChange: ({
      currentTarget: {
        value
      }
    }) => setSearch(value),
    placeholder: t('commons.communities.actions.search') || '',
    size: "small",
    slotProps: {
      input: {
        startAdornment: /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "start"
        }, /*#__PURE__*/React.createElement(SearchIcon, {
          fontSize: "small"
        })),
        endAdornment: search && /*#__PURE__*/React.createElement(_Tooltip, {
          placement: "bottom",
          title: t('commons.actions.clear')
        }, /*#__PURE__*/React.createElement(_IconButton, {
          onClick: () => setSearch(''),
          size: "small"
        }, /*#__PURE__*/React.createElement(_Close, null)))
      }
    },
    type: "text",
    value: search,
    variant: "outlined"
  })), geogroups ? geogroups.length > 0 ? /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "stretch",
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    justifyContent: "center"
  }, geogroups.map(geogroup => /*#__PURE__*/React.createElement(CommunityItem, {
    Button: Button,
    currentUser: currentUser,
    Dialog: Dialog,
    geogroup: geogroup,
    handleClick: handleClick,
    handleCommunityJoined: handleCommunityJoined,
    handleTryToJoinAsGuest: handleTryToJoinAsGuest,
    joined: Boolean(joinedGeogroups?.find(({
      group: {
        id
      }
    }) => id === geogroup.id)),
    key: geogroup.id
  }))), /*#__PURE__*/React.createElement(CreateCommunityCard, {
    createCommunityCardActions: createCommunityCardActions
  })) : !search || search.length === 1 ? /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 3,
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement("img", {
    height: 200,
    src: emptyStateIllus
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700
  }, t('commons.communities.search.title')), /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    variant: "body2"
  }, t('commons.communities.search.description')))) : /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 1,
    justifyContent: "center",
    minHeight: 200
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700,
    variant: "h6"
  }, t('commons.communities.search.empty_state_title')), /*#__PURE__*/React.createElement(_Typography, {
    align: "center"
  }, t('commons.communities.search.empty_state_description'))), /*#__PURE__*/React.createElement(CreateCommunityCard, {
    createCommunityCardActions: createCommunityCardActions
  })) : /*#__PURE__*/React.createElement(_Box, {
    alignItems: "stretch",
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    justifyContent: "center"
  }, new Array(10).fill(null).map((_, index) => /*#__PURE__*/React.createElement(CommunityItem, {
    key: index
  }))));
}